/*
===============================================================================
ABS DEVELOPERS — HIRING PAGES
File: assets/abs-hiring-pages.js

Shared interaction engine for:
  • Customer Recovery Hiring
  • Sales Hiring

Features:
  • Activates hiring-only page mode
  • Selectable job-position cards
  • Keeps selected role in a hidden input
  • Required-field validation
  • Demo mode
  • Google Sheets web-app submission mode
  • Fast success UI while Apps Script finishes in background
  • Shopify Theme Editor re-initialization
  • No external libraries
===============================================================================
*/

(() => {
  'use strict';

  const ABSHiring = window.ABSHiring || {};
  window.ABSHiring = ABSHiring;

  ABSHiring.version = '1.0.0';

  const SELECTORS = {
    page: '.abs-hiring-page',
    form: '.abs-hiring-form[data-abs-hiring-form], form.abs-hiring-form',
    position: '.abs-hiring-position',
    positionGroup: '[data-abs-position-group]',
    field: '.abs-hiring-field',
    control: '.abs-hiring-field__control',
    submit: '.abs-hiring-submit',
    success: '.abs-hiring-success'
  };

  const STATE = {
    pages: new WeakSet(),
    instances: new WeakMap()
  };

  const qs = (selector, scope = document) => scope.querySelector(selector);

  const qsa = (selector, scope = document) =>
    Array.prototype.slice.call(scope.querySelectorAll(selector));

  const text = (value) => (value == null ? '' : String(value).trim());

  const safeCSS = (value) => {
    if (window.CSS && typeof window.CSS.escape === 'function') {
      return CSS.escape(value);
    }

    return String(value).replace(/(["\\])/g, '\\$1');
  };

  const dispatch = (element, name, detail = {}) => {
    if (!element) return;

    element.dispatchEvent(
      new CustomEvent(name, {
        bubbles: true,
        detail
      })
    );
  };

  /* ==========================================================================
     1. PAGE MODE
     ========================================================================== */

  function activateHiringMode() {
    document.body.classList.add('abs-hiring-only-active');
    document.documentElement.classList.add('abs-hiring-page-loaded');
  }

  function deactivateHiringModeIfUnused() {
    if (!document.querySelector(SELECTORS.page)) {
      document.body.classList.remove('abs-hiring-only-active');
      document.documentElement.classList.remove('abs-hiring-page-loaded');
    }
  }

  /* ==========================================================================
     2. HIRING FORM CLASS
     ========================================================================== */

  class HiringPage {
    constructor(page) {
      this.page = page;
      this.form = qs(SELECTORS.form, page);

      if (!this.form) return;

      this.positions = qsa(SELECTORS.position, this.form);
      this.positionGroup = qs(SELECTORS.positionGroup, this.form);
      this.submitButton = qs(SELECTORS.submit, this.form);
      this.success = qs(SELECTORS.success, this.form);

      this.handleClick = this.handleClick.bind(this);
      this.handleInput = this.handleInput.bind(this);
      this.handleChange = this.handleChange.bind(this);
      this.handleKeydown = this.handleKeydown.bind(this);
      this.handleSubmit = this.handleSubmit.bind(this);

      this.init();
    }

    init() {
      activateHiringMode();

      this.page.dataset.absHiringReady = 'true';
      this.form.dataset.absHiringReady = 'true';

      this.positions.forEach((position) => {
        position.setAttribute(
          'role',
          position.getAttribute('role') || 'radio'
        );

        if (!position.hasAttribute('tabindex')) {
          position.setAttribute('tabindex', '0');
        }

        position.setAttribute(
          'aria-checked',
          position.classList.contains('is-selected')
            ? 'true'
            : 'false'
        );
      });

      this.form.addEventListener('click', this.handleClick);
      this.form.addEventListener('input', this.handleInput);
      this.form.addEventListener('change', this.handleChange);
      this.form.addEventListener('keydown', this.handleKeydown);
      this.form.addEventListener('submit', this.handleSubmit);

      this.syncPositionFromField();

      dispatch(this.page, 'abs:hiringready', {
        form: this.form
      });
    }

    destroy() {
      this.form.removeEventListener('click', this.handleClick);
      this.form.removeEventListener('input', this.handleInput);
      this.form.removeEventListener('change', this.handleChange);
      this.form.removeEventListener('keydown', this.handleKeydown);
      this.form.removeEventListener('submit', this.handleSubmit);
    }

    /* ==========================================================================
       3. POSITION SELECTION
       ========================================================================== */

    handleClick(event) {
      const position = event.target.closest(SELECTORS.position);

      if (!position || !this.form.contains(position)) return;

      event.preventDefault();
      this.selectPosition(position);
    }

    handleKeydown(event) {
      const position = event.target.closest(SELECTORS.position);

      if (
        position &&
        (event.key === 'Enter' || event.key === ' ')
      ) {
        event.preventDefault();
        this.selectPosition(position);
      }
    }

    selectPosition(position) {
      if (!position) return;

      const value =
        position.dataset.value ||
        position.dataset.position ||
        text(qs('.abs-hiring-position__title', position)?.textContent) ||
        text(position.textContent);

      this.positions.forEach((item) => {
        const selected = item === position;

        item.classList.toggle('is-selected', selected);
        item.setAttribute(
          'aria-checked',
          selected ? 'true' : 'false'
        );
      });

      this.writeField('position', value);

      if (this.positionGroup) {
        this.positionGroup.classList.remove('is-invalid');
      }

      dispatch(this.form, 'abs:hiringpositionchange', {
        value,
        position
      });
    }

    writeField(name, value) {
      const escaped = safeCSS(name);
      let field = qs(`[name="${escaped}"]`, this.form);

      if (!field) {
        field = document.createElement('input');
        field.type = 'hidden';
        field.name = name;
        field.dataset.absGenerated = 'true';
        this.form.appendChild(field);
      }

      field.value = value;

      field.dispatchEvent(
        new Event('change', {
          bubbles: true
        })
      );
    }

    syncPositionFromField() {
      const field = qsa('[name]', this.form).find(
        (input) => input.name === 'position'
      );

      if (!field) return;

      const selectedValue = text(field.value);

      this.positions.forEach((position) => {
        const value =
          position.dataset.value ||
          position.dataset.position ||
          text(qs('.abs-hiring-position__title', position)?.textContent) ||
          text(position.textContent);

        const selected = value === selectedValue;

        position.classList.toggle('is-selected', selected);
        position.setAttribute(
          'aria-checked',
          selected ? 'true' : 'false'
        );
      });
    }

    /* ==========================================================================
       4. FIELD HANDLING
       ========================================================================== */

    handleInput(event) {
      const control = event.target.closest(SELECTORS.control);

      if (!control || !this.form.contains(control)) return;

      this.clearControlError(control);
    }

    handleChange(event) {
      const control = event.target.closest(
        'input, select, textarea'
      );

      if (!control || !this.form.contains(control)) return;

      this.clearControlError(control);
      this.syncPositionFromField();
    }

    controlValid(control) {
      if (!control) return true;

      if (
        typeof control.checkValidity === 'function' &&
        !control.checkValidity()
      ) {
        return false;
      }

      if (control.required && !text(control.value)) {
        return false;
      }

      return true;
    }

    showControlError(control) {
      control.classList.add('is-invalid');
      control.setAttribute('aria-invalid', 'true');

      const field = control.closest(SELECTORS.field);

      if (field) {
        field.classList.add('is-invalid');
      }
    }

    clearControlError(control) {
      control.classList.remove('is-invalid');
      control.removeAttribute('aria-invalid');

      const field = control.closest(SELECTORS.field);

      if (field) {
        field.classList.remove('is-invalid');
      }
    }

    validate(showErrors = true) {
      let valid = true;

      if (this.positionGroup?.dataset.required === 'true') {
        const selected = qs(
          `${SELECTORS.position}.is-selected`,
          this.positionGroup
        );

        if (!selected) {
          valid = false;

          if (showErrors) {
            this.positionGroup.classList.add('is-invalid');
          }
        } else if (showErrors) {
          this.positionGroup.classList.remove('is-invalid');
        }
      }

      qsa(
        'input[required], select[required], textarea[required]',
        this.form
      ).forEach((control) => {
        if (!this.controlValid(control)) {
          valid = false;

          if (showErrors) {
            this.showControlError(control);
          }
        } else if (showErrors) {
          this.clearControlError(control);
        }
      });

      return valid;
    }

    focusFirstError() {
      const target =
        qs(
          '.abs-hiring-field.is-invalid .abs-hiring-field__control',
          this.form
        ) ||
        qs(
          `${SELECTORS.positionGroup}.is-invalid ${SELECTORS.position}`,
          this.form
        );

      if (!target) return;

      try {
        target.focus({
          preventScroll: false
        });
      } catch (_) {
        target.focus();
      }
    }

    /* ==========================================================================
       5. SUBMISSION
       ========================================================================== */

    async handleSubmit(event) {
      if (!this.validate(true)) {
        event.preventDefault();
        this.focusFirstError();
        return;
      }

      const mode =
        this.form.dataset.absSubmitMode ||
        this.page.dataset.absSubmitMode ||
        'demo';

      const values = this.serialize();

      dispatch(this.form, 'abs:hiringsubmit', {
        form: this.form,
        page: this.page,
        values
      });

      if (mode === 'demo') {
        event.preventDefault();
        this.showSuccess(values);
        return;
      }

      if (mode === 'google-sheets') {
        event.preventDefault();

        const endpoint = text(
          this.form.dataset.absEndpoint ||
          this.page.dataset.absEndpoint
        );

        if (!endpoint) {
          this.showSubmitError(
            'We could not send your application. Please try again.'
          );
          return;
        }

        await this.submitToGoogleSheets(endpoint, values);
      }
    }

    serialize() {
      const result = {};
      const data = new FormData(this.form);

      data.forEach((value, key) => {
        if (Object.prototype.hasOwnProperty.call(result, key)) {
          if (!Array.isArray(result[key])) {
            result[key] = [result[key]];
          }

          result[key].push(value);
        } else {
          result[key] = value;
        }
      });

      return result;
    }

    async submitToGoogleSheets(endpoint, values) {
      const button = this.submitButton;
      const originalHTML = button ? button.innerHTML : '';

      if (button) {
        button.disabled = true;
        button.setAttribute('aria-busy', 'true');
        button.textContent = 'Sending...';
      }

      this.clearSubmitError();

      try {
        const payload = new URLSearchParams();

        Object.entries(values).forEach(([key, value]) => {
          if (Array.isArray(value)) {
            value.forEach((item) => {
              payload.append(
                key,
                item == null ? '' : String(item)
              );
            });
          } else {
            payload.append(
              key,
              value == null ? '' : String(value)
            );
          }
        });

        const request = fetch(endpoint, {
          method: 'POST',
          mode: 'no-cors',
          cache: 'no-store',
          keepalive: true,
          body: payload
        });

        const result = await Promise.race([
          request
            .then(() => ({ type: 'sent' }))
            .catch((error) => ({
              type: 'error',
              error
            })),
          new Promise((resolve) => {
            window.setTimeout(
              () => resolve({ type: 'background' }),
              450
            );
          })
        ]);

        if (result.type === 'error') {
          throw result.error;
        }

        this.showSuccess(values);

        request.catch((error) => {
          console.error(
            '[ABS Hiring] Background Google Sheets request error:',
            error
          );
        });

      } catch (error) {
        console.error(
          '[ABS Hiring] Google Sheets submission failed:',
          error
        );

        this.showSubmitError(
          'We could not send your application. Please check your connection and try again.'
        );

      } finally {
        if (
          button &&
          !this.form.classList.contains('is-success')
        ) {
          button.disabled = false;
          button.removeAttribute('aria-busy');
          button.innerHTML = originalHTML;
        }
      }
    }

    showSubmitError(message) {
      let error = qs('[data-abs-hiring-submit-error]', this.form);

      if (!error) {
        error = document.createElement('p');
        error.dataset.absHiringSubmitError = 'true';
        error.setAttribute('role', 'alert');
        error.style.margin = '14px 0 0';
        error.style.color = '#efabab';
        error.style.fontSize = '11px';
        error.style.lineHeight = '1.6';

        if (this.submitButton) {
          this.submitButton.insertAdjacentElement(
            'beforebegin',
            error
          );
        } else {
          this.form.appendChild(error);
        }
      }

      error.textContent = message;
    }

    clearSubmitError() {
      const error = qs(
        '[data-abs-hiring-submit-error]',
        this.form
      );

      if (error) {
        error.remove();
      }
    }

    showSuccess(values = this.serialize()) {
      this.form.classList.add('is-success');

      if (this.success) {
        this.success.setAttribute('aria-hidden', 'false');
      }

      dispatch(this.page, 'abs:hiringsuccess', {
        values
      });

      const rect = this.form.getBoundingClientRect();

      if (
        rect.top < 40 ||
        rect.top > window.innerHeight * 0.5
      ) {
        window.scrollTo({
          top:
            window.scrollY +
            rect.top -
            Math.min(110, window.innerHeight * 0.12),
          behavior: 'smooth'
        });
      }
    }

    reset() {
      this.form.reset();

      this.positions.forEach((position) => {
        position.classList.remove('is-selected');
        position.setAttribute('aria-checked', 'false');
      });

      qsa('[data-abs-generated="true"]', this.form).forEach(
        (field) => field.remove()
      );

      qsa('.is-invalid', this.form).forEach((element) => {
        element.classList.remove('is-invalid');
        element.removeAttribute('aria-invalid');
      });

      this.form.classList.remove('is-success');

      if (this.success) {
        this.success.setAttribute('aria-hidden', 'true');
      }

      this.clearSubmitError();
    }
  }

  /* ==========================================================================
     6. INITIALIZATION
     ========================================================================== */

  function initPage(page) {
    if (!page || STATE.pages.has(page)) return;

    STATE.pages.add(page);

    const form = qs(SELECTORS.form, page);

    if (!form) return;

    const instance = new HiringPage(page);

    STATE.instances.set(page, instance);
  }

  function init(scope = document) {
    const pages = [];

    if (scope.matches && scope.matches(SELECTORS.page)) {
      pages.push(scope);
    }

    qsa(SELECTORS.page, scope).forEach((page) => {
      pages.push(page);
    });

    [...new Set(pages)].forEach(initPage);
  }

  /* ==========================================================================
     7. SHOPIFY THEME EDITOR
     ========================================================================== */

  document.addEventListener(
    'shopify:section:load',
    (event) => {
      init(event.target);
    }
  );

  document.addEventListener(
    'shopify:section:unload',
    (event) => {
      const section = event.target;

      if (!section) return;

      const pages = [];

      if (section.matches?.(SELECTORS.page)) {
        pages.push(section);
      }

      qsa(SELECTORS.page, section).forEach((page) => {
        pages.push(page);
      });

      pages.forEach((page) => {
        const instance = STATE.instances.get(page);

        if (instance) {
          instance.destroy();
          STATE.instances.delete(page);
        }
      });

      window.setTimeout(
        deactivateHiringModeIfUnused,
        0
      );
    }
  );

  /* ==========================================================================
     8. BOOT
     ========================================================================== */

  function boot() {
    init(document);
  }

  if (document.readyState === 'loading') {
    document.addEventListener(
      'DOMContentLoaded',
      boot,
      { once: true }
    );
  } else {
    boot();
  }

  /* ==========================================================================
     9. PUBLIC API
     ========================================================================== */

  ABSHiring.init = init;

  ABSHiring.get = (pageOrSelector) => {
    const page =
      typeof pageOrSelector === 'string'
        ? document.querySelector(pageOrSelector)
        : pageOrSelector;

    return page
      ? STATE.instances.get(page) || null
      : null;
  };

  ABSHiring.reset = (pageOrSelector) => {
    const instance = ABSHiring.get(pageOrSelector);

    if (instance) {
      instance.reset();
    }
  };
})();
